import { createContext, useContext, useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getRequest, postRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";
import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import Swal from "sweetalert2";

const CashierSessionContext = createContext();

export const CashierSessionProvider = ({ children }) => {
  const queryClient = useQueryClient();

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    totalPages: 1,
    filters: {},
  });

  const queryKey = useMemo(
    () => ["cashier-sessions", filterOptions.search],
    [filterOptions.search]
  );

  // Function untuk mendapatkan location-specific cashier session
  const getCurrentLocation = () => {
    return localStorage.getItem("cashier_location");
  };

  const queryString = buildQueryString({
    search: filterOptions.search || "",
    page: filterOptions.currentPage,
    paginate: 20,
    include: "cashier",
    // Hilangkan first:1 agar bisa mengambil multiple sessions
    // first:1,
    ...(filterOptions.filters["categories.id"] && {
      [`filter[categories.id]`]: filterOptions.filters["categories.id"],
    }),
  });

  const {
    data: response,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const query = buildQueryString({ search: filterOptions.search || "" });
      const res = await getRequest(`/cashier-session${queryString}`);
      if (!res.success) {
        throw new Error(res.message || "Failed to fetch cashier sessions");
      }
      return res;
    },
    keepPreviousData: true,
  });

  const addCashierSessionMutation = useMutation({
    mutationFn: async (newSession) => {
      return await postRequest(`/cashier-session`, { name: newSession });
    },
    onSuccess: () => {
      Swal.fire("Success", "Cashier session added successfully!", "success");
      queryClient.invalidateQueries({ queryKey: ["cashier-sessions"] });
    },
    onError: (err) => {
      Swal.fire(
        "Error",
        err.message || "Failed to add cashier session",
        "error"
      );
    },
  });

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setFilterOptions((prevState) => ({
        ...prevState,
        search: e.target.value,
        currentPage: 1,
      }));
    }
  };

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const totalPages = response?.pagination?.last_page || 1;

  const Pagination = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  // Function untuk mendapatkan cashier session berdasarkan location dan user
  const getCurrentLocationSession = (userId) => {
    const currentLocation = getCurrentLocation();
    const allSessions = response?.data || [];

    // Cari session yang:
    // 1. Status open
    // 2. Location sesuai dengan localStorage
    // 3. User ID sesuai (atau null untuk backward compatibility)
    const locationSession = allSessions.find(
      (session) =>
        session.status === "open" &&
        session.location === currentLocation &&
        (!session.user_id || session.user_id === userId)
    );

    return locationSession || null;
  };

  // Function untuk mendapatkan semua sessions berdasarkan location
  const getLocationSessions = (location = null) => {
    const targetLocation = location || getCurrentLocation();
    const allSessions = response?.data || [];

    return allSessions.filter((session) => session.location === targetLocation);
  };

  return (
    <CashierSessionContext.Provider
      value={{
        cashierSessions: response?.data || [], // Semua sessions untuk admin
        allCashierSessions: response?.data || [], // Alias yang lebih jelas
        getCurrentLocationSession, // Session untuk location saat ini
        getLocationSessions, // Semua sessions untuk location tertentu
        getCurrentLocation, // Helper untuk mendapatkan location saat ini
        isLoading,
        isError,
        error,
        filterOptions,
        handleSearch,
        refetchCashierSessions: refetch,
        Pagination,
        addCashierSession: addCashierSessionMutation.mutate,
      }}
    >
      {children}
    </CashierSessionContext.Provider>
  );
};

export const useCashierSession = () => {
  const context = useContext(CashierSessionContext);
  if (!context) {
    throw new Error(
      "useCashierSession must be used within CashierSessionProvider"
    );
  }

  const fetchOptions = async (inputValue) => {
    const response = await getRequest(`/cashier-session?search=${inputValue}`);
    if (response.success) {
      return response.data.map((session) => ({
        value: session.id,
        label: session.name,
      }));
    }
    return [];
  };

  return { ...context, fetchOptions };
};
